import React from 'react';
import { useTranslation } from 'react-i18next';

import { TRACKING_OPT_OUT } from '@/utils/analytics/trackingOptOut';
import { Button, DrillChevron, Icon, Sheet, Skeleton, useSheetDismiss } from '../../../../components';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { MOBILE } from '../../../../theme/tokens';
import { SlideSwap, type NavDir } from '../../../../transitions';
import { searchOptions, type IntervalOption, type OptionSearch } from '../../../home/components';
import type { TickState } from '../../selection';
import { SearchRow, SearchInput, StickySearch, TickBox } from '../PagePicker/PagePicker.styles';
import {
  Pill,
  Count,
  ShortcutPillButton,
  ShortcutLabel,
  Split,
  Rail,
  RailItem,
  RailName,
  RailValue,
  Pane,
  List,
  Row,
  RowName,
  RowValue,
  BackHeader,
  BackBtn,
  HeaderTitle,
  Options,
  OptionsRule,
  Option,
  OptionMeta,
  OptionIcon,
  GlyphDisc,
  Check,
  Empty,
  LoadingRow,
  ClearButton,
  Footer,
  OptOutScope,
} from './FiltersSheet.styles';

/** The placeholder rows' name widths while a filter's choices load (varied, like real names). */
const LOADING_ROWS = [96, 132, 78, 112];

/** Wide enough for the rail beside a pane of choices with their counts. */
const SHEET_WIDTH = '760px';

type SectionBase = {
  key: string;
  /** The filter's name (already translated). */
  title: string;
  /** Its current choice in a few words (already translated): "All countries", "Romania", "Romania, Mexico". */
  valueLabel: string;
  /** Something other than its default is picked: its current choice reads in the applied-filter orange. */
  applied?: boolean;
};

/**
 * One filter in the sheet: its choices (the pickers' own, counts included) and the one picked so far, or content of
 * its own (the page list) with the current choice's name.
 */
export type FilterSection = SectionBase &
  (
    | {
        /** The choice picked (a filter that takes one). */
        selected?: readonly string[];
        /** A filter that takes several: each choice's checkbox (ticked, empty, or a dash for a partly ticked parent or
         *  "All"). With it, `selected` is not read. */
        tick?: (value: string) => TickState;
        /** A faded hairline after this choice (the owner's own links before the affiliates). */
        dividerAfter?: string;
        options: readonly IntervalOption<string>[];
        /** A search field above the choices (both texts already translated). */
        search?: OptionSearch<string>;
        /** Its choices are still being read: placeholder rows stand in for them (and the search waits). */
        loading?: boolean;
        /** A line under the choices (already translated): why there are none, or that they could not be read. */
        note?: string;
        /** A line above the choices (already translated) that explains a dash beside some of them. The search stays. */
        hint?: string;
        content?: never;
      }
    | {
        content: React.ReactNode;
        selected?: never;
        tick?: never;
        dividerAfter?: never;
        options?: never;
        search?: never;
        loading?: never;
        note?: never;
        hint?: never;
      }
  );

export type FiltersPillProps = {
  /** How many filters are on (0: none, no count shown). */
  count: number;
  onClick: () => void;
};

export type ShortcutPillProps = {
  /** What the pill says: the filter's current choice (already translated). */
  label: string;
  /** Something is picked: the pill reads in the applied-filter orange. */
  active: boolean;
  /** Its accessible name (already translated), e.g. "Change traffic source". */
  ariaLabel: string;
  onClick: () => void;
};

/** One filter's own pill beside the filters pill (the desktop's Source), opening the sheet on that filter. */
export const ShortcutPill = ({ label, active, ariaLabel, onClick }: ShortcutPillProps): React.ReactElement => (
  <ShortcutPillButton
    {...TRACKING_OPT_OUT}
    type="button"
    onClick={onClick}
    aria-haspopup="dialog"
    aria-label={ariaLabel}
    $active={active}
  >
    <Icon name="filter" size={16} />
    <ShortcutLabel>{label}</ShortcutLabel>
  </ShortcutPillButton>
);

/** A choice's glyph (a source's, an affiliate's) on a dim disc; pass it as the choice's `icon`. */
export const OptionGlyph = ({ children }: { children: React.ReactNode }): React.ReactElement => (
  <GlyphDisc>{children}</GlyphDisc>
);

/** The one filters pill in the header, opening the sheet. */
export const FiltersPill = ({ count, onClick }: FiltersPillProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Pill type="button" onClick={onClick} aria-haspopup="dialog" $active={count > 0}>
      <Icon name="filter" size={16} />
      {t('Filters')}
      {count > 0 ? <Count>{count}</Count> : null}
    </Pill>
  );
};

export type FiltersSheetProps = {
  sections: FilterSection[];
  /** A choice was picked in a section (nothing changes on the page until Apply). */
  onPick: (section: string, value: string) => void;
  /** Puts every filter back to its default (still to apply). */
  onClear: () => void;
  /** Something is picked that Clear all would reset (else it is not offered). */
  canClear: boolean;
  /** Applies the picked filters; the sheet then closes. */
  onApply: () => void;
  onClose: () => void;
  /** The filter whose choices are on screen (null: none, the phone's list): their counts can be read now rather than
   *  with every load. */
  onSectionShow?: (key: string | null) => void;
  /** The filter to open on (a shortcut such as the desktop's Source pill); else the first one set, or the first. */
  initialKey?: string;
  message?: string;
};

/**
 * The Website tab's filters in one sheet. On a wider screen the filters sit in a rail, each with its current choice,
 * and the one picked in the rail shows its choices in the pane beside it (as the creatives editor lists versions beside
 * the one shown). On a phone the filters are a list: tapping one slides into its choices, with a back arrow in the
 * sheet's header. Choices are a draft: the page changes only on Apply, so trying a few does not reload it each time.
 */
export const FiltersSheet = ({
  sections,
  onPick,
  onClear,
  canClear,
  onApply,
  onClose,
  onSectionShow,
  initialKey,
  message,
}: FiltersSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const phone = useMediaQuery(MOBILE);
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  // The filter whose choices are shown: picked in the rail, or drilled into on a phone (null: the phone's list).
  const [activeKey, setActiveKey] = React.useState<string | null>(initialKey ?? null);
  const [dir, setDir] = React.useState<NavDir>('none');
  const [query, setQuery] = React.useState('');
  // Filters shown so far: the page list stays mounted once opened.
  const [opened, setOpened] = React.useState<ReadonlySet<string>>(() => new Set());
  // A wider screen opens on the first filter set, else the first, chosen as the sheet opens: clearing the filter being
  // edited must not swap the pane for another.
  const [firstKey] = React.useState<string | null>(() => (sections.find((s) => s.applied) ?? sections[0])?.key ?? null);
  const backRef = React.useRef<HTMLButtonElement | null>(null);
  const rowRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const returnTo = React.useRef<string | null>(null);

  // A wider screen always shows one filter: the one picked, else the one it opened on, else the first.
  const has = (key: string | null): key is string => key !== null && sections.some((s) => s.key === key);
  const shownKey = phone ? activeKey : has(activeKey) ? activeKey : has(firstKey) ? firstKey : sections[0]?.key ?? null;
  const shown = sections.find((s) => s.key === shownKey) ?? null;

  // A filter shown: kept mounted from now on (the page list), and its counts read for the choices as they stand.
  React.useEffect(() => {
    if (shownKey !== null) setOpened((cur) => (cur.has(shownKey) ? cur : new Set(cur).add(shownKey)));
  }, [shownKey]);
  React.useEffect(() => {
    onSectionShow?.(shownKey);
  }, [shownKey, onSectionShow]);

  // Keyboard focus follows the drill: onto the back arrow going in, back onto the filter's row coming out.
  React.useEffect(() => {
    if (!phone) return;
    if (activeKey !== null) backRef.current?.focus();
    else if (returnTo.current) rowRefs.current.get(returnTo.current)?.focus();
  }, [phone, activeKey]);

  const select = (key: string): void => {
    if (key === shownKey) return;
    setQuery('');
    setDir('forward');
    setActiveKey(key);
  };
  const back = (): void => {
    returnTo.current = activeKey;
    setDir('back');
    setActiveKey(null);
  };

  const choices = (section: FilterSection): React.ReactNode => {
    if (section.content !== undefined) return section.content;
    const filtered = searchOptions(section.options ?? [], section.search, query);
    return (
      <Options
        role={section.tick ? 'group' : 'listbox'}
        aria-label={section.title}
        aria-multiselectable={undefined}
        aria-busy={section.loading || undefined}
      >
        {section.search && !section.loading && !section.note ? (
          <StickySearch>
            <SearchRow>
              <Icon name="search" size={16} />
              <SearchInput
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={section.search.placeholder}
                aria-label={section.search.placeholder}
              />
            </SearchRow>
          </StickySearch>
        ) : null}
        {section.hint && !section.loading && !section.note && filtered.length > 0 ? (
          <Empty>{section.hint}</Empty>
        ) : null}
        {section.search && !section.loading && filtered.length === 0 ? <Empty>{section.search.empty}</Empty> : null}
        {filtered.map((o) => {
          const state = section.tick?.(o.value);
          const active = state ? state === 'on' : (section.selected ?? []).includes(o.value);
          return (
            <React.Fragment key={o.value}>
              <Option
                type="button"
                role={state ? 'checkbox' : 'option'}
                aria-selected={state ? undefined : active}
                aria-checked={state ? (state === 'mixed' ? 'mixed' : active) : undefined}
                // A list of checkboxes reads its picks from the boxes; a single pick keeps its fill.
                $active={!state && active}
                $nested={!!o.nested}
                onClick={() => onPick(section.key, o.value)}
              >
                <Check aria-hidden="true">
                  {state ? (
                    <TickBox $checked={state !== 'off'}>
                      {state === 'on' ? <Icon name="check" size={14} /> : null}
                      {state === 'mixed' ? <Icon name="minus" size={14} /> : null}
                    </TickBox>
                  ) : active ? (
                    <Icon name="check" size={16} />
                  ) : null}
                </Check>
                {o.icon !== undefined ? <OptionIcon aria-hidden="true">{o.icon}</OptionIcon> : null}
                {o.label}
                {o.meta !== undefined ? <OptionMeta>{o.meta}</OptionMeta> : null}
              </Option>
              {section.dividerAfter === o.value && !query.trim() ? <OptionsRule variant="styled" /> : null}
            </React.Fragment>
          );
        })}
        {section.loading
          ? LOADING_ROWS.map((width) => (
              <LoadingRow key={width} aria-hidden="true">
                <Skeleton width={18} height={14} radius={3} />
                <Skeleton width={width} height={12} radius={4} />
                <Skeleton width={22} height={10} radius={4} />
              </LoadingRow>
            ))
          : null}
        {section.note && !section.loading ? <Empty>{section.note}</Empty> : null}
      </Options>
    );
  };

  const desktopBody = (
    <Split>
      <Rail role="tablist" aria-orientation="vertical" aria-label={t('Filters')}>
        {sections.map((section) => (
          <RailItem
            key={section.key}
            type="button"
            role="tab"
            aria-selected={section.key === shownKey}
            $selected={section.key === shownKey}
            onClick={() => select(section.key)}
          >
            <RailName>{section.title}</RailName>
            <RailValue $applied={!!section.applied}>{section.valueLabel}</RailValue>
          </RailItem>
        ))}
      </Rail>
      <Pane role="tabpanel" aria-label={shown?.title}>
        {/* The page list keeps its place once opened (switching filters does not read it again). */}
        {sections
          .filter((s) => s.content !== undefined && opened.has(s.key))
          .map((s) => (
            <div key={s.key} hidden={s.key !== shownKey}>
              {s.content}
            </div>
          ))}
        {shown && shown.content === undefined ? choices(shown) : null}
      </Pane>
    </Split>
  );

  const phoneBody = (
    <SlideSwap token={shownKey ?? '__filters'} direction={dir}>
      {shown ? (
        choices(shown)
      ) : (
        <List>
          {sections.map((section) => (
            <Row
              key={section.key}
              type="button"
              ref={(el: HTMLButtonElement | null) => {
                if (el) rowRefs.current.set(section.key, el);
                else rowRefs.current.delete(section.key);
              }}
              onClick={() => select(section.key)}
            >
              <RowName>{section.title}</RowName>
              <RowValue $applied={!!section.applied}>{section.valueLabel}</RowValue>
              <DrillChevron />
            </Row>
          ))}
        </List>
      )}
    </SlideSwap>
  );

  return (
    <Sheet
      // Drilled into a filter on a phone: a back arrow and the filter's name stand in for the title.
      title={phone && shown ? undefined : 'Filters'}
      header={
        phone && shown ? (
          <BackHeader>
            <BackBtn ref={backRef} type="button" onClick={back} aria-label={t('Back to filters')}>
              <Icon name="arrow-left" size={22} />
            </BackBtn>
            <HeaderTitle>{shown.title}</HeaderTitle>
          </BackHeader>
        ) : undefined
      }
      ariaLabel="Filters"
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth={SHEET_WIDTH}
      animateResize={phone}
      footer={
        <Footer>
          {message ? <Empty role="status">{message}</Empty> : null}
          {canClear ? (
            <ClearButton type="button" onClick={onClear}>
              {t('Clear all')}
            </ClearButton>
          ) : (
            <span />
          )}
          <Button
            variant="primary"
            label={t('Apply')}
            onClick={() => {
              onApply();
              dismiss();
            }}
          />
        </Footer>
      }
    >
      <OptOutScope {...TRACKING_OPT_OUT}>{phone ? phoneBody : desktopBody}</OptOutScope>
    </Sheet>
  );
};

export default FiltersSheet;
